import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/patterns/empty-state'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  CustomerInvoiceStatusPill,
  getCustomerInvoiceBalance,
  getCustomerInvoiceRowClass,
} from '@/components/customers/customer-invoice-display'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { usePrintWindow } from '@/hooks/usePrintWindow'
import {
  customerQueries,
  useSendCustomerStatementMutation,
} from '@/lib/services/customers/customers.query'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import type {
  Customer,
  CustomerStatement,
  CustomerStatementInvoice,
} from '@/lib/services/customers/customers.types'
import { formatCalendarDate, formatCurrency, getErrorMessage } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { Mail, Printer } from 'lucide-react'
import { useState, type ComponentProps, type ReactElement, type ReactNode } from 'react'
import { toast } from 'sonner'

type StatementCustomer = Partial<
  Pick<Customer, 'id' | 'name' | 'billing_email' | 'email' | 'primary_contact'>
> & {
  organization_id?: OrganizationIdInput
}

type StatementInvoice = Pick<
  CustomerStatementInvoice,
  'id' | 'label' | 'recipient_name' | 'due_date' | 'days_past_due'
> &
  Partial<Pick<CustomerStatementInvoice, 'ordering_contact' | 'remaining_total'>> & {
    financial_state?: Pick<CustomerStatementInvoice['financial_state'], 'balance_due_total'> | null
  }

interface CustomerStatementDialogProps {
  customer?: StatementCustomer | null
  organizationId?: OrganizationIdInput
  receivables?:
    | (Pick<CustomerStatement, 'destination' | 'summary'> & { invoices: StatementInvoice[] })
    | null
  isReceivablesLoading?: boolean
  canSendEmail?: boolean
  open: boolean
  onOpenChange: NonNullable<ComponentProps<typeof Dialog>['onOpenChange']>
}

function SummaryMetric({ label, value }: { label: string; value: ReactNode }): ReactElement {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <Typography variant="label" tone="muted">
        {label}
      </Typography>
      <Typography variant="h4" className="mt-1">
        {value}
      </Typography>
    </div>
  )
}

function StatementHeaderCell({
  children,
  className = '',
}: {
  children: ReactNode
  className?: string
}): ReactElement {
  return (
    <Typography variant="eyebrow" tone="muted" weight="medium" className={className}>
      {children}
    </Typography>
  )
}

function StatementMobileLabel({ children }: { children: ReactNode }): ReactElement {
  return (
    <Typography variant="eyebrow" className="block md:hidden">
      {children}
    </Typography>
  )
}

export function CustomerStatementDialog({
  customer,
  organizationId,
  receivables,
  isReceivablesLoading = false,
  canSendEmail = false,
  open,
  onOpenChange,
}: CustomerStatementDialogProps): ReactElement {
  const queryClient = useQueryClient()
  const { openPrintWindow } = usePrintWindow({
    onError: (error) =>
      toast.error('Failed to open print window', { description: getErrorMessage(error) }),
  })
  const resolvedOrganizationId = organizationId ?? customer?.organization_id
  const sendStatementMutation = useSendCustomerStatementMutation()
  const [manualDestinationEmail, setManualDestinationEmail] = useState<string | null>(null)

  const invoices = receivables?.invoices ?? []
  const hasInvoices = invoices.length > 0
  const overdueCount = invoices.filter((invoice) => Number(invoice.days_past_due ?? 0) > 0).length
  const defaultDestination = String(
    receivables?.destination ?? customer?.billing_email ?? customer?.primary_contact?.email ?? ''
  )
  const destinationEmail = manualDestinationEmail ?? defaultDestination
  const destinationPlaceholder =
    customer?.billing_email ??
    customer?.primary_contact?.email ??
    customer?.email ??
    'billing@example.com'
  const customerId = Number(customer?.id ?? 0)

  const printStatement = async () => {
    if (!customerId || !resolvedOrganizationId) {
      return
    }

    try {
      const html = await queryClient.fetchQuery(
        customerQueries.printStatement({
          customerId,
          organizationId: resolvedOrganizationId,
        })
      )

      if (!html) {
        toast.error('Failed to load statement for printing')
        return
      }

      void openPrintWindow({ html, autoPrint: true })
    } catch (error) {
      toast.error('Failed to load statement for printing', {
        description: getErrorMessage(error),
      })
    }
  }

  const sendStatement = async () => {
    if (!customerId) {
      return
    }

    try {
      const response = await sendStatementMutation.mutateAsync({
        customerId,
        email: destinationEmail.trim() || undefined,
      })

      toast.success('Statement email queued', {
        description: response?.destination
          ? `We will send the statement to ${response.destination} shortly.`
          : 'Statement email is being sent.',
      })
    } catch (error) {
      toast.error('Failed to send statement', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          setManualDestinationEmail(null)
        }

        onOpenChange(nextOpen)
      }}
    >
      <DialogContent size="2xlarge" viewport="contained">
        <DialogHeader>
          <DialogTitle>Customer Statement</DialogTitle>
          <DialogDescription>
            Review open invoices, print a statement, or queue a statement email.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(100dvh-12rem)] pr-4">
          <div className="space-y-6">
            <div className="grid gap-3 md:grid-cols-3">
              <SummaryMetric
                label="Open Balance"
                value={formatCurrency(receivables?.summary?.open_balance_total ?? 0)}
              />
              <SummaryMetric
                label="Overdue Balance"
                value={formatCurrency(receivables?.summary?.overdue_balance_total ?? 0)}
              />
              <SummaryMetric
                label="Open Invoices"
                value={String(receivables?.summary?.open_invoice_count ?? 0)}
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Statement Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {canSendEmail ? (
                  <div className="space-y-2">
                    <Label htmlFor="statement-destination-email">Destination Email</Label>
                    <Input
                      id="statement-destination-email"
                      value={destinationEmail}
                      onChange={(event) => setManualDestinationEmail(event.target.value)}
                      placeholder={destinationPlaceholder}
                    />
                    <Typography variant="label" tone="muted">
                      We default to the billing email when set, then the primary contact, then the
                      customer email.
                    </Typography>
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={printStatement}
                    disabled={!customerId || !resolvedOrganizationId}
                  >
                    <Printer className="mr-2 size-4" />
                    Print Statement
                  </Button>
                  {canSendEmail ? (
                    <Button
                      type="button"
                      onClick={sendStatement}
                      disabled={sendStatementMutation.isPending || !customerId}
                    >
                      <Mail className="mr-2 size-4" />
                      {sendStatementMutation.isPending ? 'Sending...' : 'Email Statement'}
                    </Button>
                  ) : null}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Open Invoices</CardTitle>
              </CardHeader>
              <CardContent>
                {isReceivablesLoading ? (
                  <Typography variant="label" tone="muted">
                    Loading statement...
                  </Typography>
                ) : hasInvoices ? (
                  <div className="overflow-hidden rounded-md border">
                    <div className="hidden grid-cols-[minmax(0,1fr)_8rem_7rem_7.25rem] gap-4 border-b bg-muted/10 px-4 py-2.5 md:grid">
                      <StatementHeaderCell>Order</StatementHeaderCell>
                      <StatementHeaderCell>Due</StatementHeaderCell>
                      <StatementHeaderCell className="text-right">Balance</StatementHeaderCell>
                      <StatementHeaderCell className="text-right">Status</StatementHeaderCell>
                    </div>
                    <div className="divide-y">
                      {invoices.map((invoice) => {
                        const remainingTotal = getCustomerInvoiceBalance(invoice)

                        return (
                          <div
                            key={invoice.id}
                            className={getCustomerInvoiceRowClass(
                              invoice,
                              'grid grid-cols-2 gap-3 rounded-none border-y-0 border-r-0 px-4 py-3 md:grid-cols-[minmax(0,1fr)_8rem_7rem_7.25rem] md:items-center'
                            )}
                          >
                            <div className="col-span-2 min-w-0 md:col-span-1">
                              <Typography variant="body" weight="medium">
                                {invoice.label}
                              </Typography>
                              <Typography variant="label" tone="muted">
                                {invoice.ordering_contact ||
                                  invoice.recipient_name ||
                                  'No order contact'}
                              </Typography>
                            </div>
                            <div className="min-w-0">
                              <StatementMobileLabel>Due</StatementMobileLabel>
                              <span className="whitespace-nowrap text-sm tabular-nums">
                                {formatCalendarDate(invoice.due_date, 'MMM D, YYYY', '-')}
                              </span>
                            </div>
                            <div className="min-w-0 md:text-right">
                              <StatementMobileLabel>Balance</StatementMobileLabel>
                              <span className="text-sm font-semibold tabular-nums">
                                {formatCurrency(remainingTotal)}
                              </span>
                            </div>
                            <div className="col-span-2 flex items-start md:col-span-1 md:justify-end">
                              <CustomerInvoiceStatusPill invoice={invoice} />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <div className="border-t bg-muted/10 px-4 py-3">
                      <Typography variant="label" tone="muted">
                        {overdueCount > 0
                          ? `${overdueCount} open invoice${overdueCount === 1 ? '' : 's'} currently overdue.`
                          : 'All open invoices are still current.'}
                      </Typography>
                    </div>
                  </div>
                ) : (
                  <EmptyState
                    size="compact"
                    title="No open invoices for this customer right now."
                  />
                )}
              </CardContent>
            </Card>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
